import { useTranslation } from 'react-i18next'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useActiveDeal } from '@order/hooks/useDeal'
import { ErpFrame } from '@ca/common'
import { FrameStatus, StockUtils } from '@frame/StockUtils'
import FrameUtils from '@frame/FrameUtils'
import { OrderValues, PriceValue } from '@ca/common/types'
import FrameTile from '@frame/Frame/FrameTile'
import { FrameTileProps } from './types'
import { statusToBadge, statusToVariant } from './styles'
import { useFrameDetails } from './useFrameDetails'

interface Props extends Partial<FrameTileProps> {
  frame: ErpFrame
  status?: FrameStatus
  dropdown?: React.ReactNode
  orderValues?: OrderValues
}

export default function FrameItem({
  frame,
  dropdown,
  onPress,
  orderValues,
  ...props
}: Props) {
  const { t } = useTranslation(['common', 'status'])
  const config = useRemoteConfig()
  const activeDeal = useActiveDeal()
  const { details } = useFrameDetails(frame)

  const defaultFrameCondition = config?.configConditions?.['frames:default']
  const validation = activeDeal?.frameRequirements?.length
    ? {
        requires: activeDeal.frameRequirements,
        condition: null,
      }
    : defaultFrameCondition
  const limits = FrameUtils.getLimits({
    frame,
    values: orderValues,
    config,
    minPrice: PriceValue.get(
      activeDeal?.settings?.minFramePrice ?? activeDeal?.minFramePrice,
      config?.region
    ),
    maxPrice: PriceValue.get(activeDeal?.settings?.maxFramePrice, config?.region),
  })

  const { status: frameStatus, disabled } = StockUtils.getFrameStatus(
    frame,
    config?.region,
    {
      minPrice: limits.minPrice,
      maxPrice: limits.maxPrice,
      validation,
      context: validation && config
        ? {
            conditions: config.conditions,
          }
        : undefined,
    }
  )

  const status = props.status || frameStatus
  const variant =
    props.variant || (disabled ? 'disabled' : statusToVariant[status])

  const title = frame.default_code || frame.id.toString()

  const badge = statusToBadge[status]
    ? {
        variant: statusToBadge[status],
        text: t(`status:frame.${status}`),
      }
    : undefined

  return (
    <FrameTile
      {...props}
      title={title}
      onPress={onPress}
      image={frame.url}
      details={details}
      badge={badge}
      dropdown={dropdown}
      variant={variant}
    />
  )
}
